<template>
  <div class="troodon-toast" :class="`troodon-toast--${type}`">
    <span v-if="$slots.icon" class="troodon-toast__icon">
      <slot name="icon" />
    </span>
    <p class="troodon-toast__text"><slot /></p>
    <span v-if="$slots.link" class="troodon-toast__link">
      <slot name="link" />
    </span>
    <button type="button" class="troodon-toast__close" aria-label="閉じる" @click="emit('close')">
      <IconXLine :size="24" color="currentColor" />
    </button>
    <span class="troodon-toast__timeout-bar" :style="{ animationDuration: `${timeout}ms` }" />
  </div>
</template>

<script setup lang="ts">
import '../../styles/troodon/_variables.scss'

import IconXLine from '~/components/icon/x-line.vue'

withDefaults(
  defineProps<{
    type?: 'info' | 'attention' | 'error'
    timeout?: number
  }>(),
  {
    type: 'info',
    timeout: 3000,
  }
)

const emit = defineEmits<{
  (e: 'close'): void
}>()
</script>

<style lang="scss" scoped>
.troodon-toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 920px;
  padding: 16px;
  overflow: hidden;
  border-radius: 4px;
  background-color: var(--troodon-c-white);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 8px 24px rgba(0, 0, 0, 0.12);
  color: var(--troodon-c-text-high);

  &--attention {
    padding: 14px;
    border: 2px solid var(--troodon-c-attention);
    background-color: var(--troodon-c-attention-surface);
  }

  &--error {
    gap: 10px;
    background-color: var(--troodon-c-error);
    color: var(--troodon-c-white);
  }

  &__icon {
    display: flex;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: var(--troodon-c-primary);
  }

  &--error &__icon {
    color: var(--troodon-c-white);
  }

  &__text {
    display: -webkit-box;
    flex: 1 0 0;
    min-width: 1px;
    margin: 0;
    overflow: hidden;
    font-size: var(--troodon-f-size-sm);
    font-weight: 400;
    line-height: var(--troodon-lh-input);
    word-break: break-word;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__link {
    display: flex;
    flex-shrink: 0;
  }

  &--error &__link :deep(.troodon-link) {
    color: var(--troodon-c-white);
  }

  &__close {
    display: flex;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    background-color: transparent;
    color: var(--troodon-c-text-placeholder);
    cursor: pointer;
  }

  &--error &__close {
    color: var(--troodon-c-white);
  }

  &__timeout-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 4px;
    background-color: var(--troodon-c-text-placeholder);
    animation-name: troodon-toast-countdown;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
  }

  &--error &__timeout-bar {
    background-color: var(--troodon-c-white);
  }
}

@keyframes troodon-toast-countdown {
  from {
    width: 100%;
  }

  to {
    width: 0;
  }
}
</style>
